import { Alert, Rate, Skeleton } from 'antd'
import dayjs from 'dayjs'

import { OpenResourceCardTask } from '@/Core/Tasks/Resources/OpenResourceCardTask'
import { cx } from '@/Ship/Helpers/cssClass'

import { AssignmentResource } from '@/Containers/AssignmentSection/AssignmentContainer/Resources/AssignmentResource'

import type { RatingGroupBy, RatingItem } from '../Contracts/Report'
import { useReportFetch } from '../Hooks/useReportFetch'

import styles from './Report.module.css'

/** Ліміт деталізації на бекенді (DispatcherRatingReportAction::RATINGS_LIMIT) */
const LIMIT = 500

interface RatingItemsProps {
    groupBy: RatingGroupBy
    groupKey: string
    params: Record<string, string>
}

/**
 * Оцінки групи (розгорнутий рядок рейтингу): дата · зірки · коментар · задача й «інша
 * сторона» (для диспетчера — водій, для водія — диспетчер), новіші зверху.
 */
export const RatingItems = ({ groupBy, groupKey, params }: RatingItemsProps) => {
    const { data, loading, error } = useReportFetch<{ items: RatingItem[] }>(
        '/services/reports/dispatcher-rating/ratings',
        { ...params, groupBy, key: groupKey }
    )
    const items = data?.items ?? []

    if (error)
        return (
            <div className={cx(styles, 'drill')}>
                <Alert type="error" showIcon title={error} />
            </div>
        )
    if (loading && !data)
        return (
            <div className={cx(styles, 'drill')}>
                <Skeleton active paragraph={{ rows: 2 }} title={false} />
            </div>
        )
    if (items.length === 0)
        return (
            <div className={cx(styles, 'drill')}>
                <span className={cx(styles, 'faint')}>Оцінок немає</span>
            </div>
        )

    return (
        <div className={cx(styles, 'drill')}>
            <p className={cx(styles, 'drillTitle')}>Оцінки групи, новіші зверху</p>
            <ul className={cx(styles, 'ratingList')}>
                {items.map((item) => {
                    const counterpart = groupBy === 'dispatcher' ? item.driverName : item.dispatcherName
                    return (
                        <li key={item.id} className={cx(styles, 'ratingRow')}>
                            <span className={cx(styles, 'muted')}>
                                {item.ratingDate ? dayjs(item.ratingDate).format('DD.MM.YYYY') : '—'}
                            </span>
                            <span className={cx(styles, 'rating')}>
                                <Rate disabled value={item.rating} count={5} />
                                <span className={cx(styles, 'muted')}>{item.rating}/5</span>
                            </span>
                            <p className={cx(styles, 'ratingComment')}>
                                {item.comment ?? <span className={cx(styles, 'faint')}>Без коментаря</span>}
                            </p>
                            <span className={cx(styles, 'ratingMeta')}>
                                {counterpart && <>{counterpart} · </>}
                                <button
                                    type="button"
                                    className={cx(styles, 'nameLink')}
                                    onClick={() =>
                                        OpenResourceCardTask({
                                            resource: AssignmentResource,
                                            primaryFieldValue: item.assignmentUuid,
                                            mode: 'view',
                                        })
                                    }
                                >
                                    Задача #{item.assignmentId}
                                </button>
                            </span>
                        </li>
                    )
                })}
            </ul>
            {items.length >= LIMIT && (
                <div className={cx(styles, 'drillNote')}>Показано перші {LIMIT} оцінок — звузьте фільтри.</div>
            )}
        </div>
    )
}
